import { z } from 'zod'

const CronFieldSchema = z.union([z.string(), z.number()])

/** AWS EventBridge cron expression: `cron(minute hour day-of-month month day-of-week year)` */
export type CronExpression = string

export const CronFieldsSchema = z.object({
  minute: CronFieldSchema,
  hour: CronFieldSchema,
  dayOfMonth: CronFieldSchema,
  month: CronFieldSchema,
  dayOfWeek: CronFieldSchema,
  year: CronFieldSchema,
})
export type CronFields = z.infer<typeof CronFieldsSchema>

export const CRON_DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const
export type CronDayOfWeek = (typeof CRON_DAYS_OF_WEEK)[number]

export const CRON_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const
export type CronMonth = (typeof CRON_MONTHS)[number]

/**
 * Builds a validated AWS EventBridge cron expression string from individual fields.
 *
 * Format: `cron(minute hour day-of-month month day-of-week year)`
 *
 * @example
 * buildCron({ minute: 0, hour: 10, dayOfMonth: '*', month: '*', dayOfWeek: '?', year: '*' })
 * // → 'cron(0 10 * * ? *)'
 */
export function buildCron(fields: CronFields): CronExpression {
  const minute = String(fields.minute)
  const hour = String(fields.hour)
  const dayOfMonth = String(fields.dayOfMonth)
  const month = String(fields.month)
  const dayOfWeek = String(fields.dayOfWeek)
  const year = String(fields.year)

  validateField(minute, 'minute')
  validateField(hour, 'hour')
  validateField(dayOfMonth, 'dayOfMonth')
  validateField(month, 'month')
  validateField(dayOfWeek, 'dayOfWeek')
  validateField(year, 'year')
  validateDayFields(dayOfMonth, dayOfWeek)

  return `cron(${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek} ${year})`
}

const FIELD_LIMITS: Record<keyof CronFields, { min: number; max: number }> = {
  minute: { min: 0, max: 59 },
  hour: { min: 0, max: 23 },
  dayOfMonth: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  dayOfWeek: { min: 1, max: 7 },
  year: { min: 1970, max: 2199 },
}

function isDayName(value: string): value is CronDayOfWeek {
  return CRON_DAYS_OF_WEEK.includes(value as CronDayOfWeek)
}

function isMonthName(value: string): value is CronMonth {
  return CRON_MONTHS.includes(value as CronMonth)
}

function dayNameToNumber(name: CronDayOfWeek): number {
  return CRON_DAYS_OF_WEEK.indexOf(name) + 1
}

function monthNameToNumber(name: CronMonth): number {
  return CRON_MONTHS.indexOf(name) + 1
}

function resolveFieldValue(value: string | number, fieldName: keyof CronFields): number {
  if (typeof value === 'number') return value
  const upper = value.toUpperCase()
  if (fieldName === 'dayOfWeek' && isDayName(upper)) return dayNameToNumber(upper)
  if (fieldName === 'month' && isMonthName(upper)) return monthNameToNumber(upper)
  const num = parseInt(value, 10)
  if (isNaN(num)) throw new Error(`Invalid cron field value '${value}' for ${fieldName}`)
  return num
}

function assertRange(value: number, fieldName: keyof CronFields): void {
  const { min, max } = FIELD_LIMITS[fieldName]
  if (value < min || value > max) {
    throw new Error(`${fieldName} value ${value} is out of range (${min}-${max})`)
  }
}

// Validates a single token within a cron field (e.g. "5", "MON", "1-5", "*/10")
function validateToken(token: string, fieldName: keyof CronFields): void {
  if (token === '*' || token === '?') return

  if (token.includes('/')) {
    const [base, stepStr] = token.split('/')
    if (base !== '*') validateToken(base, fieldName)
    const step = parseInt(stepStr, 10)
    if (isNaN(step) || step <= 0) throw new Error(`Invalid step '${stepStr}' in ${fieldName}`)
    return
  }

  if (token.includes('-')) {
    const [startStr, endStr] = token.split('-')
    const start = resolveFieldValue(startStr, fieldName)
    const end = resolveFieldValue(endStr, fieldName)
    assertRange(start, fieldName)
    assertRange(end, fieldName)
    if (start > end) throw new Error(`Invalid range ${startStr}-${endStr} in ${fieldName}: start > end`)
    return
  }

  if (fieldName === 'dayOfMonth' && /^(\d+W|L(-\d+)?|LW)$/.test(token)) return
  if (fieldName === 'dayOfWeek' && /^[1-7]L$/.test(token)) return
  if (fieldName === 'dayOfWeek' && /^[1-7]#[1-5]$/.test(token)) return

  const num = resolveFieldValue(token, fieldName)
  assertRange(num, fieldName)
}

function validateField(value: string, fieldName: keyof CronFields): void {
  if (value === '*' || value === '?') return
  const parts = value.split(',')
  for (const part of parts) {
    validateToken(part.trim(), fieldName)
  }
}

function validateDayFields(dayOfMonth: string, dayOfWeek: string): void {
  const domIsWild = dayOfMonth === '*' || dayOfMonth === '?'
  const dowIsWild = dayOfWeek === '*' || dayOfWeek === '?'
  if (!domIsWild && !dowIsWild) {
    throw new Error('AWS cron requires exactly one of dayOfMonth or dayOfWeek to be "?" when the other is specified')
  }
}
